// Centralized Google Analytics helpers for SPA tracking and funnel events.
import { buildRuntimePlaybookPath } from '../lib/playbookRoutes';
import { getRuntimeSiteContext } from '../lib/siteContext';

const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID ||
  process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
  '';

const isBrowser = typeof window !== 'undefined';
const ITINERARY_ATTRIBUTION_KEY = 'alakai_itinerary_attribution';
const DASHBOARD_SESSION_KEY = 'alakai_dashboard_session_id';
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  'https://us-central1-alakaialohaadventures-782c9.cloudfunctions.net';
const DASHBOARD_EVENT_LOG_URL = `${FUNCTIONS_BASE_URL}/dashboardEventLog`;
let hasWarnedMissingId = false;

const DASHBOARD_EVENT_NAMES = new Set([
  'island_chooser_start',
  'island_chooser_submit',
  'island_chooser_result',
  'island_chooser_result_viewed',
  'island_chooser_next_step',
  'homebase_quiz_start',
  'homebase_quiz_complete',
  'homebase_quiz_result',
  'quiz_start',
  'quiz_complete',
  'quiz_result_viewed',
  'quiz_to_interview',
  'interview_started',
  'interview_completed',
  'itinerary_preview_ready',
  'itinerary_unlock_started',
  'itinerary_unlock_purchase',
  'itinerary_unlocked',
  'itinerary_pilot_offer_viewed',
  'itinerary_pilot_unlock',
  'itinerary_promo_unlock',
  'itinerary_full_ready',
  'itinerary_action',
  'purchase',
  'flow_error',
]);

const DASHBOARD_SENSITIVE_KEY_RE = /(email|e-mail|phone|name|contact|note|notes|message|dream|context|free.?text|description|prompt|comment)/i;

const isAdmin = () => {
  if (!isBrowser) return false;
  const hostname = window.location.hostname;
  const search = window.location.search || '';
  return (
    hostname === 'localhost' ||
    process.env.NODE_ENV === 'development' ||
    search.includes('admin=true')
  );
};

const isGaAvailable = () => {
  if (!isBrowser) return false;
  if (!GA_MEASUREMENT_ID) {
    if (!hasWarnedMissingId && process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] Missing GA measurement ID. Set REACT_APP_GA_MEASUREMENT_ID.');
      hasWarnedMissingId = true;
    }
    return false;
  }

  if (typeof window.gtag !== 'function') {
    return false;
  }

  return true;
};

const withMeasurementId = (params = {}) => {
  if (!GA_MEASUREMENT_ID || params.send_to) {
    return params;
  }

  return { send_to: GA_MEASUREMENT_ID, ...params };
};

const pruneUndefined = (obj = {}) => {
  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  });
  return result;
};

const classifyDashboardSurface = (pathname = '') => {
  const segments = String(pathname || '').split('/').filter(Boolean);
  const first = segments[0] || '';
  const joined = segments.join('/');
  if (!segments.length) return 'home';
  if (first === 'quiz') return 'quiz';
  if (first === 'interviews' || first === 'interview') return 'interview';
  if (first === 'itinerary' || first === 'itineraries') return 'itinerary';
  if (first === 'checkout' || first === 'unlock') return 'checkout';
  if (first === 'blog') return 'blog';
  if (first === 'guides' || first === 'guide') return 'guide';
  if (first === 'activities' || first === 'activity') return 'activity';
  if (first === 'restaurants' || first === 'restaurant' || first === 'food') return 'restaurant';
  if (first === 'lodging' || first === 'hotels' || first === 'stays') return 'lodging';
  if (first === 'areas' || joined.includes('/areas/')) return 'area';
  if (first === 'regions' || joined.includes('/regions/')) return 'region';
  if (first === 'playbook' || joined.includes('playbook')) return 'playbook';
  if (['kauai', 'oahu', 'maui', 'big-island', 'big_island'].includes(first)) return 'island';
  return 'other';
};

const getDashboardSessionId = () => {
  if (!isBrowser) return null;
  try {
    const existing = window.sessionStorage.getItem(DASHBOARD_SESSION_KEY);
    if (existing) return existing;
    const generated =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(DASHBOARD_SESSION_KEY, generated);
    return generated;
  } catch {
    return null;
  }
};

const sanitizeDashboardValue = (value, depth = 2) => {
  if (depth < 0 || value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.slice(0, 180);
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeDashboardValue(item, depth - 1)).filter((item) => item !== undefined);
  }
  if (typeof value === 'object') {
    const result = {};
    Object.entries(value).forEach(([key, child]) => {
      if (DASHBOARD_SENSITIVE_KEY_RE.test(key)) return;
      const sanitized = sanitizeDashboardValue(child, depth - 1);
      if (sanitized !== undefined) result[key] = sanitized;
    });
    return Object.keys(result).length ? result : undefined;
  }
  return undefined;
};

const pickDashboardFields = (params = {}, keys = []) => {
  const result = {};
  keys.forEach((key) => {
    const value = sanitizeDashboardValue(params[key], 1);
    if (value !== undefined) result[key] = value;
  });
  return result;
};

const trackDashboardEvent = (eventName, params = {}) => {
  if (!isBrowser || !DASHBOARD_EVENT_NAMES.has(eventName)) return;
  try {
    const context = getRuntimeSiteContext();
    const route = window.location.pathname || '/';
    const islandKey =
      params.island_key ||
      params.islandKey ||
      (context.siteVariant === 'apex' ? undefined : context.island);
    const payload = pruneUndefined({
      eventName,
      occurredAt: new Date().toISOString(),
      siteVariant: context.siteVariant || 'unknown',
      islandKey,
      surface: classifyDashboardSurface(route),
      route,
      pageUrl: window.location.href?.split('#')[0],
      referrer: document.referrer || undefined,
      anonymousSessionId: getDashboardSessionId(),
      attribution: pickDashboardFields(params, [
        'source',
        'medium',
        'campaign',
        'term',
        'content',
        'gclid',
        'src',
        'spoke',
        'landing_path',
        'slug_path',
      ]),
      relatedIds: pickDashboardFields(params, [
        'choice_id',
        'edited_from_choice_id',
        'interview_id',
        'itinerary_id',
        'itinerary_version',
        'transaction_id',
        'payment_intent_id',
      ]),
      dimensions: sanitizeDashboardValue(params, 2),
      metrics: pickDashboardFields(params, ['value', 'full_days', 'month']),
    });
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(DASHBOARD_EVENT_LOG_URL, new Blob([body], { type: 'application/json' }));
      if (sent) return;
    }
    fetch(DASHBOARD_EVENT_LOG_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] dashboard event logging failed', error);
    }
  }
};

const parseAttributionInput = (input) => {
  if (!input) return {};
  if (input instanceof URLSearchParams) {
    return Object.fromEntries(input.entries());
  }
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch (error) {
      return {};
    }
  }
  if (typeof input === 'object') {
    return { ...input };
  }
  return {};
};

export const extractItineraryAttributionFromLocation = (search = '', pathname = '') => {
  try {
    const params = new URLSearchParams(search || '');
    const landingPath = params.get('landingPath') || pathname || '/interview/wizard';

    const utmParams = {};
    params.forEach((value, key) => {
      if (key.startsWith('utm_') && value) {
        utmParams[key] = value;
      }
    });

    const attribution = {};
    const srcOverride = params.get('src');
    const source = params.get('utm_source') || srcOverride;
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const term = params.get('utm_term');
    const content = params.get('utm_content');
    const gclid = params.get('gclid');
    const spoke = params.get('spoke');

    if (source) attribution.source = source;
    if (medium) attribution.medium = medium;
    if (campaign) attribution.campaign = campaign;
    if (term) attribution.term = term;
    if (content) attribution.content = content;
    if (gclid) attribution.gclid = gclid;
    if (spoke) attribution.spoke = spoke;
    if (srcOverride) attribution.src = srcOverride;

    if (Object.keys(utmParams).length > 0) {
      attribution.utmParams = utmParams;
      if (!attribution.source && utmParams.utm_source) attribution.source = utmParams.utm_source;
      if (!attribution.medium && utmParams.utm_medium) attribution.medium = utmParams.utm_medium;
      if (!attribution.campaign && utmParams.utm_campaign) attribution.campaign = utmParams.utm_campaign;
      if (!attribution.term && utmParams.utm_term) attribution.term = utmParams.utm_term;
      if (!attribution.content && utmParams.utm_content) attribution.content = utmParams.utm_content;
    }

    const hasSignal = Boolean(
      attribution.source ||
      attribution.medium ||
      attribution.campaign ||
      attribution.term ||
      attribution.content ||
      attribution.gclid ||
      attribution.spoke ||
      attribution.src ||
      Object.keys(utmParams).length > 0
    );

    if (!hasSignal) {
      return null;
    }

    attribution.landingPath = landingPath;
    attribution.createdAt = new Date().toISOString();

    return attribution;
  } catch (error) {
    console.warn('Failed to extract attribution params from URL', error);
    return null;
  }
};

export const buildEventParamsFromAttribution = (input) => {
  const base = parseAttributionInput(input);
  const utm = parseAttributionInput(base?.utmParams);
  return pruneUndefined({
    landing_path: base.landingPath ?? base.landing_path,
    spoke: base.spoke ?? base.slug_path,
    src: base.src,
    source: base.source ?? base.src ?? utm.utm_source ?? base.utm_source,
    medium: base.medium ?? utm.utm_medium ?? base.utm_medium,
    campaign: base.campaign ?? utm.utm_campaign ?? base.utm_campaign,
    term: base.term ?? utm.utm_term ?? base.utm_term,
    content: base.content ?? utm.utm_content ?? base.utm_content,
    gclid: base.gclid,
    axis_type: base.axis_type ?? base.axisType,
    axis_value: base.axis_value ?? base.axisValue,
    slug_path: base.slug_path ?? base.spoke,
    interview_id: base.interview_id ?? base.interviewId,
  });
};

export const saveItineraryAttribution = (payload) => {
  if (!isBrowser) return;
  const existing = buildEventParamsFromAttribution(readItineraryAttribution());
  const incoming = buildEventParamsFromAttribution(payload);
  const merged = pruneUndefined({
    ...existing,
    ...incoming,
  });
  if (Object.keys(merged).length === 0) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      ITINERARY_ATTRIBUTION_KEY,
      JSON.stringify({
        ...merged,
        storedAt: Date.now(),
      }),
    );
  } catch (error) {
    console.warn('[analytics] Unable to persist itinerary attribution', error);
  }
};

export const readItineraryAttribution = () => {
  if (!isBrowser) return null;
  try {
    const raw = window.sessionStorage.getItem(ITINERARY_ATTRIBUTION_KEY);
    return raw ? parseAttributionInput(raw) : null;
  } catch (error) {
    console.warn('[analytics] Unable to read itinerary attribution', error);
    return null;
  }
};

export const clearItineraryAttribution = () => {
  if (!isBrowser) return;
  try {
    window.sessionStorage.removeItem(ITINERARY_ATTRIBUTION_KEY);
  } catch (error) {
    console.warn('[analytics] Unable to clear itinerary attribution', error);
  }
};

export const mergeItineraryAttribution = (params = {}) => {
  const cleanedParams = pruneUndefined(params);
  const storedRaw = readItineraryAttribution();
  const stored = buildEventParamsFromAttribution(storedRaw);
  if (!stored || Object.keys(stored).length === 0) {
    return cleanedParams;
  }

  return pruneUndefined({
    ...stored,
    ...cleanedParams,
  });
};

export const buildItineraryAttributionSearchParams = (existingSearch = '') => {
  const params =
    typeof existingSearch === 'string'
      ? new URLSearchParams(existingSearch)
      : new URLSearchParams();

  if (!isBrowser) {
    return params;
  }

  const storedRaw = readItineraryAttribution();
  if (!storedRaw || typeof storedRaw !== 'object') {
    return params;
  }

  const stored = parseAttributionInput(storedRaw);
  const eventParams = buildEventParamsFromAttribution(storedRaw);

  const landingPath =
    stored.landingPath ??
    stored.landing_path ??
    eventParams.landing_path ??
    (stored.slug_path ? buildRuntimePlaybookPath(stored.slug_path) : undefined);
  if (landingPath && !params.has('landingPath')) {
    params.set('landingPath', landingPath);
  }

  const spoke =
    stored.spoke ??
    eventParams.spoke ??
    stored.slug_path ??
    eventParams.slug_path ??
    stored.axis_value ??
    eventParams.axis_value;
  if (spoke && !params.has('spoke')) {
    params.set('spoke', spoke);
  }

  const srcValue =
    stored.src ??
    eventParams.src ??
    stored.source ??
    eventParams.source;
  if (srcValue && !params.has('src')) {
    params.set('src', srcValue);
  }

  const gclid = stored.gclid ?? eventParams.gclid;
  if (gclid && !params.has('gclid')) {
    params.set('gclid', gclid);
  }

  const utmParams = parseAttributionInput(stored.utmParams);
  Object.entries(utmParams).forEach(([key, value]) => {
    if (!params.has(key) && value) {
      params.set(key, value);
    }
  });

  return params;
};

export const buildItineraryAttributionSearchString = (existingSearch = '') => {
  const params = buildItineraryAttributionSearchParams(existingSearch);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

const normalizePageViewArgs = (pathOrOptions, maybeParams) => {
  if (pathOrOptions && typeof pathOrOptions === 'object') {
    const { path, title, location, params = {} } = pathOrOptions;
    return {
      path,
      title,
      location,
      params: {
        ...params,
        ...(typeof maybeParams === 'object' ? maybeParams : {}),
      },
    };
  }

  return {
    path: typeof pathOrOptions === 'string' ? pathOrOptions : undefined,
    params: {
      ...(typeof maybeParams === 'object' ? maybeParams : {}),
    },
  };
};

export const trackPageView = (pathOrOptions, maybeParams) => {
  if (isAdmin() || !isGaAvailable()) return;

  const options = normalizePageViewArgs(pathOrOptions, maybeParams);
  const pagePath =
    options.path ??
    (isBrowser ? window.location.pathname + window.location.search : undefined);
  const pageLocation =
    options.location ?? (isBrowser ? window.location.href : undefined);
  const pageTitle =
    options.title ?? (isBrowser && typeof document !== 'undefined'
      ? document.title
      : undefined);

  const payload = withMeasurementId({
    page_path: pagePath,
    page_location: pageLocation,
    page_title: pageTitle,
    ...options.params,
  });

  window.gtag('event', 'page_view', payload);
};

export const trackEvent = (eventName, paramsOrCategory = {}, label, value) => {
  if (!eventName || isAdmin()) return;

  let params = {};
  if (
    paramsOrCategory &&
    typeof paramsOrCategory === 'object' &&
    !Array.isArray(paramsOrCategory)
  ) {
    params = { ...paramsOrCategory };
  } else {
    if (paramsOrCategory) params.event_category = paramsOrCategory;
    if (label) params.event_label = label;
    if (typeof value === 'number') params.value = value;
  }

  try {
    const pilotId = isBrowser && window.sessionStorage.getItem('alakai_itinerary_pilot_id');
    if (pilotId && !params.pilot_id) params.pilot_id = pilotId;
  } catch {
    // Analytics must not block the trip flow when storage is unavailable.
  }
  trackDashboardEvent(eventName, params);

  if (!isGaAvailable()) return;

  window.gtag('event', eventName, withMeasurementId(params));
};

export const trackHomebaseGenerated = (area, region, scenario) => {
  trackEvent('homebase_generated', {
    axis_region: region,
    axis_area: area,
    scenario,
  });
  trackEvent('homebase_scenario', { scenario });
};

export const trackInterviewCompleted = (interviewId, extra = {}) => {
  trackEvent('interview_completed', {
    interview_id: interviewId,
    ...extra,
  });
};

export const trackItineraryViewed = (interviewId, extra = {}) => {
  trackEvent('itinerary_viewed', {
    interview_id: interviewId,
    ...extra,
  });
};
